import React, { useId } from 'react';
import type { TOptions } from 'i18next';
import { Card, Spinner, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { LoginStreakHome } from '../services/api';

export type LoginStreakSafeT = (key: string, fallback: string, options?: TOptions) => string;

export interface LoginStreakSidebarCardProps {
  data: LoginStreakHome | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  safeT: LoginStreakSafeT;
  /** `mobile`: faixa compacta nos stats no celular; `desktop`: cartão na sidebar (padrão). */
  variant?: 'desktop' | 'mobile';
}

const LoginStreakSidebarCard: React.FC<LoginStreakSidebarCardProps> = ({
  data,
  loading,
  error,
  onRetry,
  safeT,
  variant = 'desktop',
}) => {
  const tooltipId = useId().replace(/:/g, '');

  const infoBody = safeT(
    'loginStreak.infoModalBody',
    'Play at least once per day to grow your streak. If you miss a day, the streak resets to zero.'
  );

  const infoTooltip = (
    <Tooltip id={`login-streak-info-${tooltipId}`} className="login-streak-dark-tooltip">
      {infoBody}
    </Tooltip>
  );

  const streakInfoAria = safeT('loginStreak.infoAria', 'How the streak works');

  const desktopInfoButton = (
    <OverlayTrigger
      placement="top"
      trigger={['hover', 'focus', 'click']}
      rootClose
      overlay={infoTooltip}
    >
      <Button
        type="button"
        variant="light"
        size="sm"
        className="login-streak-info-btn rounded-circle border shadow-sm"
        aria-label={streakInfoAria}
      >
        <i className="bi bi-info-circle" aria-hidden />
      </Button>
    </OverlayTrigger>
  );

  const desktopBody = loading ? (
    <div className="text-center py-2" role="status" aria-live="polite">
      <Spinner animation="border" size="sm" />
    </div>
  ) : error ? (
    <div className="px-1">
      <p className="small text-warning mb-2">{safeT('loginStreak.loadError', 'Could not load streak.')}</p>
      <Button variant="link" size="sm" className="p-0 small" onClick={onRetry}>
        {safeT('loginStreak.retry', 'Try again')}
      </Button>
    </div>
  ) : (
    <>
      <div className="login-streak-hero text-center mb-2">
        <div className="login-streak-count text-primary" aria-live="polite">
          {data?.currentStreak ?? 0}
        </div>
        <div className="login-streak-subtitle text-primary small fw-bold text-uppercase">
          {safeT('loginStreak.dayStreakLabel', 'Day streak', { count: data?.currentStreak ?? 0 })}
        </div>
      </div>
      <div className="login-streak-week rounded border p-2 mb-2 bg-light">
        <div className="login-streak-weekdays d-flex justify-content-between text-center small mb-2 px-1">
          {(data?.weekDays ?? []).map((d) => (
            <span
              key={d.dayOfWeek}
              className={d.isToday ? 'text-primary fw-semibold' : 'text-muted'}
            >
              {safeT(`loginStreak.weekdayShort.${d.dayOfWeek}`, String(d.dayOfWeek))}
            </span>
          ))}
        </div>
        <div className="login-streak-dots d-flex justify-content-between px-1">
          {(data?.weekDays ?? []).map((d) => (
            <span
              key={`dot-${d.dayOfWeek}`}
              className={`login-streak-dot ${d.completed ? 'login-streak-dot--done' : ''} ${
                d.isToday ? 'login-streak-dot--today' : ''
              }`}
              aria-label={
                d.completed
                  ? safeT('loginStreak.dayDoneAria', 'Completed')
                  : safeT('loginStreak.dayPendingAria', 'Not completed')
              }
            >
              {d.completed ? <i className="bi bi-check-lg" aria-hidden /> : null}
            </span>
          ))}
        </div>
      </div>
    </>
  );

  const mobileBody = loading ? (
    <div className="login-streak-compact-loading text-center py-1" role="status" aria-live="polite">
      <Spinner animation="border" size="sm" />
    </div>
  ) : error ? (
    <div className="login-streak-compact-error px-1">
      <p className="login-streak-compact-error-text text-warning mb-1">{safeT('loginStreak.loadError', 'Could not load streak.')}</p>
      <Button variant="link" size="sm" className="p-0 small" onClick={onRetry}>
        {safeT('loginStreak.retry', 'Try again')}
      </Button>
    </div>
  ) : (
    <div className="login-streak-compact-body">
      <div className="login-streak-compact-hero">
        <span className="login-streak-compact-count text-primary" aria-live="polite">
          {data?.currentStreak ?? 0}
        </span>
        <span className="login-streak-compact-subtitle text-primary text-uppercase fw-bold">
          {safeT('loginStreak.dayStreakLabel', 'Day streak', { count: data?.currentStreak ?? 0 })}
        </span>
      </div>
      <div className="login-streak-compact-dots d-flex justify-content-between">
        {(data?.weekDays ?? []).map((d) => (
          <span
            key={`m-dot-${d.dayOfWeek}`}
            className={`login-streak-dot login-streak-dot--compact ${d.completed ? 'login-streak-dot--done' : ''} ${
              d.isToday ? 'login-streak-dot--today' : ''
            }`}
            aria-label={
              d.completed
                ? safeT('loginStreak.dayDoneAria', 'Completed')
                : safeT('loginStreak.dayPendingAria', 'Not completed')
            }
          >
            {d.completed ? <i className="bi bi-check-lg" aria-hidden /> : null}
          </span>
        ))}
      </div>
    </div>
  );


  if (variant === 'mobile') {
    if (loading || error) {
      return <div className="login-streak-compact">{mobileBody}</div>;
    }
    return (
      <OverlayTrigger placement="top" trigger={['click', 'focus']} rootClose overlay={infoTooltip}>
        <div
          className="login-streak-compact login-streak-compact--tap-tooltip"
          role="button"
          tabIndex={0}
          aria-label={streakInfoAria}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              (e.currentTarget as HTMLElement).click();
            }
          }}
        >
          {mobileBody}
        </div>
      </OverlayTrigger>
    );
  }

  return (
    <div className="login-streak-sidebar mb-2 d-none d-lg-block">
      <Card className="top-players-card-sidebar login-streak-sidebar-card position-relative">
        {desktopInfoButton}
        <Card.Header className="p-2 text-center">
          <small className="fw-bold">{safeT('loginStreak.title', 'Streak')}</small>
        </Card.Header>
        <Card.Body className="p-2">{desktopBody}</Card.Body>
      </Card>
    </div>
  );
};

export default LoginStreakSidebarCard;
